import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import Clickwrap from '../components/Clickwrap';
import { useToast } from '../components/Toast';
import { fetchAgreements, acceptAgreements } from '../data/agreements';
import { useAuth } from '../AuthContext';
import { createEntity } from '../data/entities';
import { useMemberDeals, useVisibleDeals } from '../data/permissions';
import {
  useOrg,
  useClaimableOrgs,
  useMyInvites,
  useInviteJoin,
  claimOrg,
  createOrg,
} from '../data/orgs';
import {
  DEFAULT_PARTIES,
  PROJECT_TYPES,
  projectTypeOf,
  statusLabel,
  appendAudit,
  actorOf,
} from '../data/deals';

// Filter chips stay on one or two lines. A selected tag stays visible when
// the row is collapsed so it can be turned off.
const FILTER_TAG_LIMIT = 8;
const CARD_TAG_LIMIT = 3;

const STATUS_TONE = {
  draft: '',
  negotiating: 'b-info',
  agreed: 'b-ok',
  executed: 'b-ok',
  closed: '',
};

// Lobby for signed-in users with no org yet: claim a seeded org, accept an
// invite, or create a fresh organization. Invites addressed to my exact
// email auto-accept — the invitee lands straight in the org, no extra click.
// Exported: the Home dashboard renders the same lobby for org-less users.
export function OrgLobby() {
  const { user } = useAuth();
  const { memberships } = useOrg();
  const claimable = useClaimableOrgs(memberships);
  const invites = useMyInvites();
  const { joiningName, acceptError, accept } = useInviteJoin();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [manualWait, setManualWait] = useState(false);
  const [dpaDocs, setDpaDocs] = useState(null); // DPA shown before the org is created
  const toast = useToast();

  const doClaim = async (org) => {
    setBusy(true);
    try {
      await claimOrg(org, user);
      toast(`You now own ${org.name}`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const doAccept = async (invite) => {
    setBusy(true);
    setManualWait(true);
    const ok = await accept(invite);
    if (!ok) setManualWait(false);
    setBusy(false);
  };

  // Creating an organization = becoming a customer entity, so the data
  // processing addendum is read and accepted here (scroll-to-end clickwrap,
  // same component as signup). The org is created only after Agree; the
  // acceptance is recorded against the new org (orgs/{id}.dpa).
  const doCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const docs = await fetchAgreements('dpa');
      if (!docs.length) throw new Error('empty');
      setDpaDocs(docs);
    } catch {
      toast('The data processing addendum could not be loaded — please try again in a moment.', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const finishCreate = async (seen) => {
    setBusy(true);
    try {
      const orgId = await createOrg(name.trim(), user);
      await acceptAgreements(seen.map((d) => d.id), { orgId });
      setDpaDocs(null);
      toast('Organization created', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  // An invite is being provisioned — show progress, not the create-org form
  // (showing "create an organization" to a just-invited teammate reads as
  // "your invite didn't work"). A failed accept drops back to the card.
  const waitingOnInvite = !acceptError && (joiningName || manualWait || invites.length > 0);
  if (waitingOnInvite) {
    return (
      <div className="cs-page u-stack m-rise" style={{ '--gap': 'var(--sp-5)', maxWidth: 560 }}>
        <div>
          <h1>Welcome to fast.legal</h1>
          <p className="u-muted">Your invitation was found — setting up your access.</p>
        </div>
        <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <strong>Joining {joiningName || invites[0]?.orgName || 'your organization'}…</strong>
          <span className="u-small u-muted">
            Provisioning your membership and project access. This takes a few
            seconds — you'll land in your workspace automatically.
          </span>
          <div className="cs-skeleton" style={{ height: 8 }} />
        </div>
      </div>
    );
  }

  if (dpaDocs) {
    return (
      <div className="cs-page u-stack m-rise" style={{ '--gap': 'var(--sp-5)', maxWidth: 880 }}>
        <div>
          <h1>Create {name.trim()}</h1>
          <p className="u-muted">
            One document before the organization exists: how fast.legal handles your organization's content, who can see it, and what you can hold us to.
          </p>
        </div>
        <Clickwrap
          docs={dpaDocs}
          busy={busy}
          intro="Read to the end, then agree on behalf of the organization you are creating."
          onAgree={finishCreate}
        />
        <div>
          <button type="button" className="cs-btn cs-btn-ghost" disabled={busy} onClick={() => setDpaDocs(null)}>Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="cs-page u-stack m-rise" style={{ '--gap': 'var(--sp-6)', maxWidth: 560 }}>
      <div>
        <h1>Welcome to fast.legal</h1>
        <p className="u-muted">
          Projects live inside an organization. Claim yours, accept an invite, or create one.
        </p>
      </div>

      {claimable.map((org) => (
        <div key={org.id} className="cs-card u-split">
          <div>
            <strong>{org.name}</strong>
            <div className="u-small u-muted">Reserved for {org.ownerEmail}</div>
          </div>
          <button className="cs-btn cs-btn-primary" disabled={busy} onClick={() => doClaim(org)}>
            Claim ownership
          </button>
        </div>
      ))}

      {acceptError && (
        <div className="cs-banner b-danger" role="status">{acceptError}</div>
      )}

      {invites.map((inv) => (
        <div key={inv.id} className="cs-card u-split">
          <div>
            <strong>{inv.orgName || 'Organization'}</strong>
            <div className="u-small u-muted">Invited as {inv.role}</div>
          </div>
          <button className="cs-btn cs-btn-primary" disabled={busy} onClick={() => doAccept(inv)}>
            Accept invite
          </button>
        </div>
      ))}

      <form className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)' }} onSubmit={doCreate}>
        <div className="cs-field">
          <label htmlFor="org-name">Create a new organization</label>
          <input
            id="org-name"
            className="cs-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. HGM"
          />
        </div>
        <button className="cs-btn cs-btn-outline" type="submit" disabled={busy || !name.trim()}>
          Review the data processing addendum and create
        </button>
      </form>
    </div>
  );
}

function ProjectTagChips({ tags, limit = CARD_TAG_LIMIT }) {
  const [open, setOpen] = useState(false);
  if (!tags?.length) return null;
  const shown = open || tags.length <= limit ? tags : tags.slice(0, limit);
  return (
    <>
      {shown.map((tag) => (
        <span key={tag} className="cs-chip" style={{ opacity: 0.8 }}>#{tag}</span>
      ))}
      {tags.length > limit && (
        <button
          type="button"
          className="cs-chip"
          style={{ cursor: 'pointer', opacity: 0.7 }}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'fewer' : `+${tags.length - limit}`}
        </button>
      )}
    </>
  );
}

function NewProjectModal({ orgId, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const [name, setName] = useState('');
  const [typeKey, setTypeKey] = useState('deal');
  const [dealType, setDealType] = useState('');
  const [tags, setTags] = useState('');
  const [parties, setParties] = useState(DEFAULT_PARTIES.map((p) => p.label).join(', '));
  const [busy, setBusy] = useState(false);

  const ptype = PROJECT_TYPES.find((t) => t.key === typeKey) || PROJECT_TYPES[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      // Negotiated projects have negotiation sides; authored projects are a
      // single team (partyKey 'team' on every participant).
      const partyList = ptype.negotiated
        ? parties
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .map((label) => ({
            key: label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
            label,
          }))
        : [];
      const tagList = tags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 12);
      // Pre-allocate the id so the immutable S3 prefix can reference it (the
      // raw bucket only accepts keys under items/ — see uploadServer.js).
      const { db } = await import('../firebaseConfig');
      const dealId = db.collection('deals').doc().id;
      await createEntity('deal', { org: orgId }, {
        name: name.trim(),
        projectType: typeKey,
        tags: tagList,
        dealType: dealType.trim(),
        status: 'draft',
        parties: partyList,
        milestones: [],
        s3Prefix: `items/${dealId}/`,
        // Project visibility: the creator sees their own project; org
        // admins see everything unless the project is later marked
        // restricted (Team tab). Rules require the bool at create.
        memberUids: [user.uid],
        restricted: false,
        createdBy: user.uid,
      }, { id: dealId });
      // Creator joins as admin (on the first party for negotiated projects).
      await createEntity(
        'participant',
        { org: orgId, deal: dealId },
        {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || (user.email || '').split('@')[0],
          partyKey: partyList[0]?.key || 'team',
          dealRole: 'admin',
          addedBy: user.uid,
        },
        { id: user.uid }
      );
      await appendAudit({ org: orgId, deal: dealId }, actorOf(user, null), 'deal.created', `Project created: "${name.trim()}" (${ptype.label})`);
      toast('Project created', 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New project" onClose={onClose}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={submit}>
        <div className="cs-field">
          <label htmlFor="proj-name">Project name</label>
          <input
            id="proj-name"
            className="cs-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={ptype.negotiated ? 'Project Alpine — Share Purchase' : 'Orion Platform — Functional Design'}
            autoFocus
          />
        </div>
        <div className="cs-field">
          <label htmlFor="proj-type">Project type</label>
          <select
            id="proj-type"
            className="cs-input"
            value={typeKey}
            onChange={(e) => setTypeKey(e.target.value)}
          >
            {PROJECT_TYPES.map((t) => (
              <option key={t.key} value={t.key}>{t.icon} {t.label}</option>
            ))}
          </select>
          <div className="cs-help">{ptype.hint}</div>
        </div>
        <div className="cs-field">
          <label htmlFor="proj-subtype">{ptype.negotiated ? 'Agreement type' : 'Label'}</label>
          <input
            id="proj-subtype"
            className="cs-input"
            value={dealType}
            onChange={(e) => setDealType(e.target.value)}
            placeholder={ptype.negotiated ? 'SPA, MSA, Operating Agreement…' : 'v2 spec, Q3 update, Series A book…'}
          />
        </div>
        <div className="cs-field">
          <label htmlFor="proj-tags">Tags (comma-separated)</label>
          <input
            id="proj-tags"
            className="cs-input"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="HGM, HoldCo, IP, Nevada…"
          />
          <div className="cs-help">Free-form labels for filtering and grouping related projects.</div>
        </div>
        {ptype.negotiated && (
          <div className="cs-field">
            <label htmlFor="proj-parties">Parties (comma-separated)</label>
            <input
              id="proj-parties"
              className="cs-input"
              value={parties}
              onChange={(e) => setParties(e.target.value)}
            />
            <div className="cs-help">Buyer, Seller, Senior Lender… For an operating agreement: Company, Members.</div>
          </div>
        )}
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !name.trim()}>
            Create project
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function Deals() {
  const { org, orgId, memberships } = useOrg();
  const { deals, isOrgAdmin } = useVisibleDeals();
  // Tag chips follow membership, not the admin's full project list.
  const { deals: memberDeals } = useMemberDeals({ enabled: isOrgAdmin });
  const [showNew, setShowNew] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Deep link from the shell ("Create" in the sidebar, Home action card):
  // /projects?new=1 opens the New Project modal, then strips the param.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') {
      setShowNew(true);
      navigate('/projects', { replace: true });
    }
  }, [location.search, navigate]);
  const [typeFilter, setTypeFilter] = useState('');
  const [tagFilters, setTagFilters] = useState([]);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [q, setQ] = useState('');
  // Document/artifact titles per project, fetched lazily the first time a
  // search runs so the search box reaches INSIDE projects — "find every
  // document or artifact for a given company", then narrow with the chips.
  const [docIndex, setDocIndex] = useState(null); // { [dealId]: [{ id, title, kind }] }

  useEffect(() => {
    setDocIndex(null);
  }, [orgId]);

  useEffect(() => {
    if (!q.trim() || docIndex !== null || !(deals || []).length) return undefined;
    let alive = true;
    (async () => {
      try {
        const { db } = await import('../firebaseConfig');
        const entries = await Promise.all(
          deals.map(async (d) => {
            const snap = await db.collection(`orgs/${orgId}/deals/${d.id}/docs`).get();
            return [d.id, snap.docs.map((x) => ({
              id: x.id,
              title: x.data().title || '',
              kind: x.data().kind || 'primary',
            }))];
          })
        );
        if (alive) setDocIndex(Object.fromEntries(entries));
      } catch {
        if (alive) setDocIndex({});
      }
    })();
    return () => { alive = false; };
  }, [q, deals, orgId, docIndex]);

  // Drop a selected tag once it is no longer on a project this user is on.
  const tagKey = (isOrgAdmin ? memberDeals : deals) == null
    ? null
    : [...new Set(((isOrgAdmin ? memberDeals : deals) || []).flatMap((d) => d.tags || []).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
      .join('\n');
  useEffect(() => {
    if (tagKey == null) return;
    const allowed = new Set(tagKey.split('\n').filter(Boolean));
    setTagFilters((cur) => {
      const next = cur.filter((t) => allowed.has(t));
      return next.length === cur.length ? cur : next;
    });
  }, [tagKey]);

  if (memberships === null) {
    return (
      <div className="cs-page">
        <div className="cs-skeleton" style={{ height: 120 }} />
      </div>
    );
  }
  if (!memberships.length) return <OrgLobby />;

  // Filter chips only for types in the visible list, and only for tags on
  // projects this user is a member of. Admins still see every project card;
  // the tag row does not pick up tags from projects they were never added to.
  const typesInUse = PROJECT_TYPES.filter((t) => (deals || []).some((d) => projectTypeOf(d).key === t.key));
  const tagSource = isOrgAdmin ? memberDeals : deals;
  const tagsInUse = tagSource
    ? [...new Set(tagSource.flatMap((d) => d.tags || []).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    : [];
  const myDealIds = new Set((tagSource || []).map((d) => d.id));

  const needle = q.trim().toLowerCase();
  const docMatchesFor = (dealId) =>
    needle ? (docIndex?.[dealId] || []).filter((doc) => doc.title.toLowerCase().includes(needle)) : [];
  const matchesSearch = (d) => {
    if (!needle) return true;
    const hay = [
      d.name,
      d.description,
      d.dealType,
      projectTypeOf(d).label,
      ...(d.tags || []),
      ...(d.parties || []).map((p) => p.label),
    ].join(' ').toLowerCase();
    return hay.includes(needle) || docMatchesFor(d.id).length > 0;
  };
  const shown = (deals || []).filter(
    (d) =>
      (!typeFilter || projectTypeOf(d).key === typeFilter) &&
      tagFilters.every((t) => (d.tags || []).includes(t)) &&
      matchesSearch(d)
  );
  const toggleTag = (tag) =>
    setTagFilters((cur) => (cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag]));

  return (
    <div className="cs-page u-stack m-rise" style={{ '--gap': 'var(--sp-6)' }}>
      <div className="cs-page-head">
        <div>
          <h1>Projects</h1>
          <p className="u-muted">{org?.name || '…'} — deals, agreement sets, design docs, decks, and everything in between</p>
        </div>
        <button className="cs-btn cs-btn-primary" onClick={() => setShowNew(true)}>
          New project
        </button>
      </div>

      {/* Search + filters — search reaches project names, tags, parties, AND
          document/artifact titles inside every project; chips narrow the
          results by project type and tags (tags combine, AND semantics). */}
      <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <input
          className="cs-input"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search projects, documents, artifacts… (e.g. a company name)"
          aria-label="Search projects and documents"
          style={{ maxWidth: 440 }}
        />
        {(typesInUse.length > 1 || tagsInUse.length > 0) && (
          <div className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)' }}>
            {typesInUse.length > 1 && (
              <>
                <button
                  type="button"
                  className="cs-chip"
                  style={typeFilter === '' ? { borderColor: 'var(--brand)' } : { cursor: 'pointer' }}
                  onClick={() => setTypeFilter('')}
                >
                  All types
                </button>
                {typesInUse.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className="cs-chip"
                    style={typeFilter === t.key ? { borderColor: 'var(--brand)' } : { cursor: 'pointer' }}
                    onClick={() => setTypeFilter(typeFilter === t.key ? '' : t.key)}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </>
            )}
            {(() => {
              const pinned = tagsInUse.filter((t) => tagFilters.includes(t));
              const rest = tagsInUse.filter((t) => !tagFilters.includes(t));
              const room = Math.max(FILTER_TAG_LIMIT, pinned.length);
              const visible = tagsExpanded ? tagsInUse : [...pinned, ...rest].slice(0, room);
              const hidden = tagsInUse.length - visible.length;
              return (
                <>
                  {visible.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className="cs-chip"
                      aria-pressed={tagFilters.includes(tag)}
                      style={tagFilters.includes(tag) ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
                      onClick={() => toggleTag(tag)}
                    >
                      #{tag}
                    </button>
                  ))}
                  {tagsInUse.length > FILTER_TAG_LIMIT && (
                    <button
                      type="button"
                      className="cs-chip"
                      style={{ cursor: 'pointer', opacity: 0.7 }}
                      onClick={() => setTagsExpanded((o) => !o)}
                    >
                      {tagsExpanded ? 'fewer' : `+${hidden} more`}
                    </button>
                  )}
                </>
              );
            })()}
            {(typeFilter || tagFilters.length > 0 || needle) && (
              <button
                type="button"
                className="cs-chip"
                style={{ cursor: 'pointer', opacity: 0.7 }}
                onClick={() => { setTypeFilter(''); setTagFilters([]); setQ(''); }}
              >
                ✕ Clear
              </button>
            )}
          </div>
        )}
        {needle && deals && (
          <span className="u-small u-muted">
            {shown.length} project{shown.length === 1 ? '' : 's'} match
            {docIndex === null && ' — searching inside documents…'}
          </span>
        )}
      </div>

      {deals === null && <div className="cs-skeleton" style={{ height: 160 }} />}
      {deals && !deals.length && (
        <div className="cs-empty">
          No projects yet. A project holds one matter — an agreement negotiation, a presentation,
          a filing set — with its documents, versions, deal points, and its own AI brain.
          Create one, then upload the working document (or let the drafting agent author it).
          Tip: press <kbd style={{ padding: '0 4px' }}>⌘K</kbd> anytime to ask how anything works.
        </div>
      )}
      {deals && deals.length > 0 && !shown.length && (
        <div className="cs-empty">
          Nothing matches{needle ? ` "${q.trim()}"` : ''} with the current filters — clear a chip or broaden the search.
        </div>
      )}

      <div className="u-grid" style={{ '--gap': 'var(--sp-4)' }}>
        {shown.map((deal) => {
          const ptype = projectTypeOf(deal);
          const docHits = docMatchesFor(deal.id);
          return (
            <div key={deal.id} className="cs-card u-stack" style={{ '--gap': 'var(--sp-2)' }}>
              <Link to={`/deals/${deal.id}`} className="u-stack" style={{ '--gap': 'var(--sp-2)', textDecoration: 'none', color: 'inherit' }}>
                <div className="u-split">
                  <strong>{deal.name}</strong>
                  <span className={`cs-badge ${STATUS_TONE[deal.status] || ''}`}>{statusLabel(deal)}</span>
                </div>
                <span className="u-small u-muted">
                  {ptype.icon} {ptype.label}
                  {deal.dealType ? ` · ${deal.dealType}` : ''}
                </span>
                <div className="u-cluster u-small u-muted" style={{ '--gap': 'var(--sp-2)' }}>
                  {(deal.parties || []).map((p) => (
                    <span key={p.key} className="cs-chip">{p.label}</span>
                  ))}
                </div>
                <div className="u-small u-muted u-tabular">
                  {deal.docCount || 0} document{(deal.docCount || 0) === 1 ? '' : 's'}
                </div>
              </Link>
              {myDealIds.has(deal.id) && (deal.tags || []).length > 0 && (
                <div className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)' }}>
                  <ProjectTagChips tags={deal.tags} />
                </div>
              )}
              {/* Documents/artifacts inside this project that match the search
                  — each jumps straight to the document. */}
              {docHits.length > 0 && (
                <div className="u-stack u-small" style={{ '--gap': 'var(--sp-1)', borderTop: '1px solid var(--border)', paddingTop: 'var(--sp-2)' }}>
                  {docHits.slice(0, 5).map((doc) => (
                    <Link
                      key={doc.id}
                      to={`/deals/${deal.id}/docs/${doc.id}`}
                      className="u-split"
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      title="Open this document"
                    >
                      <span>📄 {doc.title}</span>
                      <span className="u-xs u-muted">{doc.kind}</span>
                    </Link>
                  ))}
                  {docHits.length > 5 && (
                    <span className="u-xs u-muted">+{docHits.length - 5} more in this project</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showNew && <NewProjectModal orgId={orgId} onClose={() => setShowNew(false)} />}
    </div>
  );
}
